import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause } from "react-icons/fa";

function shouldFetchAsBlob(url) {
  if (!url || url.startsWith("blob:")) return true;
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

export default function VoiceNote({ audioUrl }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const createdBlobUrlRef = useRef(null);

  useEffect(() => {
    setError(null);
    const revokeCreated = () => {
      const u = createdBlobUrlRef.current;
      if (u) {
        URL.revokeObjectURL(u);
        createdBlobUrlRef.current = null;
      }
    };

    const run = async () => {
      if (!audioUrl?.trim()) return;
      if (audioUrl.startsWith("blob:")) {
        revokeCreated();
        setObjectUrl(audioUrl);
        return;
      }
      if (!shouldFetchAsBlob(audioUrl)) {
        revokeCreated();
        setObjectUrl(null);
        return;
      }
      try {
        const response = await fetch(audioUrl, { credentials: "same-origin", cache: "no-store" });
        if (!response.ok) throw new Error("Failed to fetch audio");
        const blob = await response.blob();
        revokeCreated();
        const url = URL.createObjectURL(blob);
        createdBlobUrlRef.current = url;
        setObjectUrl(url);
      } catch (err) {
        console.error("Error fetching audio blob:", err);
        revokeCreated();
        setObjectUrl(null);
      }
    };

    void run();
    return () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      revokeCreated();
      setObjectUrl(null);
    };
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => setError("Playback failed. Please try again."));
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const seekPosition = (e.clientX - rect.left) / rect.width;
    audio.currentTime = seekPosition * duration;
  };

  const formatTime = (time) => {
    if (isNaN(time)) time = 0;
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const effectiveSrc = objectUrl ?? audioUrl;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="relative flex w-full min-w-0 max-w-[min(100%,15.5rem)] items-center gap-2 rounded-xl bg-black px-2.5 py-2 shadow-md sm:max-w-md sm:gap-2.5 sm:px-3 sm:py-2.5">
        <button
          type="button"
          onClick={togglePlay}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black transition-opacity hover:opacity-90"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <FaPause className="h-3.5 w-3.5" /> : <FaPlay className="h-3.5 w-3.5 translate-x-px" />}
        </button>
        <div
          className="relative h-9 min-w-[4.5rem] flex-1 cursor-pointer overflow-hidden rounded-md bg-[#1a1a1a]"
          onClick={handleSeek}
        >
          <div
            className="h-full bg-white/25 transition-[width] duration-150 ease-out"
            style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
          />
        </div>
        <div className="shrink-0 text-[11px] font-semibold tabular-nums text-white sm:text-xs">
          {isPlaying ? formatTime(currentTime) : formatTime(duration)}
        </div>
      </div>
      <audio
        ref={audioRef}
        src={effectiveSrc}
        preload="metadata"
        hidden
        onLoadedMetadata={() => {
          const audio = audioRef.current;
          if (audio && !isNaN(audio.duration) && audio.duration !== Infinity) {
            setDuration(audio.duration);
          }
          setError(null);
        }}
        onTimeUpdate={() => {
          const audio = audioRef.current;
          if (audio) setCurrentTime(audio.currentTime);
        }}
        onEnded={() => setIsPlaying(false)}
        onError={() => setError("Could not load this voice note. Try again later.")}
      />
      {error ? <div className="text-xs text-red-400">{error}</div> : null}
    </div>
  );
}
